import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { stringify, parse } from 'querystringify';
import { extension, command, NodeExtension, ExtensionTag, omitExtraAttributes, cx, findSelectedNodeOfType, object } from '@remirror/core';
import { ResizableNodeView, ResizableRatioType } from 'prosemirror-resizable-view';

/**
 * ResizableIframeView is a NodeView for iframe. You can resize the iframe by
 * dragging the handle over the iframe.
 */
class ResizableIframeView extends ResizableNodeView {
  constructor(node, view, getPos, iframeOptions) {
    var _node$attrs = node.attrs,
        width = _node$attrs.width,
        height = _node$attrs.height;
    super({
      node,
      view,
      getPos,
      aspectRatio: ResizableRatioType.Flexible,
      options: iframeOptions,
      initialSize: {
        width,
        height
      }
    });
    this.options = iframeOptions;
  }

  createElement(_ref) {
    var node = _ref.node,
        options = _ref.options;
    var _node$attrs2 = node.attrs,
        src = _node$attrs2.src,
        type = _node$attrs2.type,
        allowFullScreen = _node$attrs2.allowFullScreen,
        frameBorder = _node$attrs2.frameBorder;
    var iframe = document.createElement('iframe');
    iframe.setAttribute('src', src);
    iframe.dataset.embedType = type;
    iframe.setAttribute('allowfullscreen', allowFullScreen ? 'true' : 'false');
    iframe.setAttribute('frameBorder', frameBorder === null || frameBorder === void 0 ? void 0 : frameBorder.toString());

    if (options !== null && options !== void 0 && options.class) {
      iframe.classList.add(options.class);
      iframe.classList.add("".concat(options.class, "-").concat(type));
    }

    iframe.style.width = '100%';
    iframe.style.height = '100%';
    return iframe;
  }

}

var _excluded = ["frameBorder", "allowFullScreen", "src", "type"];

var _dec, _dec2, _dec3, _dec4, _dec5, _class, _class2;
var IframeExtension = (_dec = extension({
  defaultOptions: {
    defaultSource: '',
    class: 'remirror-iframe',
    enableResizing: false
  },
  staticKeys: ['defaultSource', 'class']
}), _dec2 = command(), _dec3 = command(), _dec4 = command(), _dec5 = command(), _dec(_class = (_class2 = class IframeExtension extends NodeExtension {
  get name() {
    return 'iframe';
  }

  createTags() {
    return [ExtensionTag.Block];
  }

  createNodeViews() {
    var iframeOptions = _objectSpread({}, this.options);

    if (this.options.enableResizing) {
      return (node, view, getPos) => {
        return new ResizableIframeView(node, view, getPos, iframeOptions);
      };
    }

    return {};
  }

  createNodeSpec(extra, override) {
    var _override$parseDOM;

    var defaultSource = this.options.defaultSource;
    return _objectSpread(_objectSpread({
      selectable: false
    }, override), {}, {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        src: defaultSource ? {
          default: defaultSource
        } : {},
        allowFullScreen: {
          default: true
        },
        frameBorder: {
          default: 0
        },
        type: {
          default: 'custom'
        },
        width: {
          default: null
        },
        height: {
          default: null
        }
      }),
      parseDOM: [{
        tag: 'iframe',
        getAttrs: dom => {
          var frameBorder = dom.getAttribute('frameborder');
          return _objectSpread(_objectSpread({}, extra.parse(dom)), {}, {
            type: dom.getAttribute('data-embed-type'),
            height: dom.getAttribute('height'),
            width: dom.getAttribute('width'),
            allowFullScreen: dom.getAttribute('allowfullscreen') === 'false' ? false : true,
            frameBorder: frameBorder ? Number.parseInt(frameBorder, 10) : 0,
            src: dom.getAttribute('src')
          });
        }
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: node => {
        var _omitExtraAttributes = omitExtraAttributes(node.attrs, extra),
            frameBorder = _omitExtraAttributes.frameBorder,
            allowFullScreen = _omitExtraAttributes.allowFullScreen,
            src = _omitExtraAttributes.src,
            type = _omitExtraAttributes.type,
            rest = _objectWithoutProperties(_omitExtraAttributes, _excluded);

        var className = this.options.class;
        return ['iframe', _objectSpread(_objectSpread(_objectSpread({}, extra.dom(node)), rest), {}, {
          class: cx(className, "".concat(className, "-").concat(type)),
          src,
          'data-embed-type': type,
          allowfullscreen: allowFullScreen ? 'true' : 'false',
          frameBorder: frameBorder === null || frameBorder === void 0 ? void 0 : frameBorder.toString()
        })];
      }
    });
  }
  /**
   * Add a custom iFrame to the editor.
   */


  addIframe(attributes) {
    return _ref => {
      var tr = _ref.tr,
          dispatch = _ref.dispatch;
      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.replaceSelectionWith(this.type.create(attributes)));
      return true;
    };
  }
  /**
   * Add a YouTube embedded iFrame to the editor.
   */


  addYouTubeVideo(props) {
    return this.addIframe({
      src: createYouTubeUrl(props),
      frameBorder: 0,
      type: 'youtube',
      allowFullScreen: 'true'
    });
  }
  /**
   * Update the iFrame source for the currently selected video.
   */


  updateIframeSource(src) {
    return _ref2 => {
      var tr = _ref2.tr,
          dispatch = _ref2.dispatch;
      var iframeNode = findSelectedNodeOfType({
        selection: tr.selection,
        types: this.type
      }); // Selected node is NOT an iframe node, return false indicating this command is NOT enabled

      if (!iframeNode) {
        return false;
      } // Call dispatch method if present (using optional chaining), to modify the actual document


      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setNodeMarkup(iframeNode.pos, undefined, _objectSpread(_objectSpread({}, iframeNode.node.attrs), {}, {
        src
      }))); // Return true, indicating this command IS enabled

      return true;
    };
  }
  /**
   * Update the YouTube video iFrame.
   */


  updateYouTubeVideo(props) {
    return this.updateIframeSource(createYouTubeUrl(props));
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "addIframe", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "addIframe"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "addYouTubeVideo", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "addYouTubeVideo"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "updateIframeSource", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "updateIframeSource"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "updateYouTubeVideo", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "updateYouTubeVideo"), _class2.prototype)), _class2)) || _class);

/**
 * A Url parser that relies on the browser for the majority of the work.
 */
function parseUrl(url) {
  var parser = document.createElement('a'); // Let the browser do the work

  parser.href = url;
  var searchObject = parse(parser.search);
  return {
    protocol: parser.protocol,
    host: parser.host,
    hostname: parser.hostname,
    port: parser.port,
    pathname: parser.pathname,
    search: parser.search,
    searchObject,
    hash: parser.hash
  };
}

function createYouTubeUrl(props) {
  var _parseUrl$searchObjec, _parseUrl, _parseUrl$searchObjec2;

  var video = props.video,
      _props$enhancedPrivac = props.enhancedPrivacy,
      enhancedPrivacy = _props$enhancedPrivac === void 0 ? true : _props$enhancedPrivac,
      _props$showControls = props.showControls,
      showControls = _props$showControls === void 0 ? true : _props$showControls,
      _props$startAt = props.startAt,
      startAt = _props$startAt === void 0 ? 0 : _props$startAt;
  var id = (_parseUrl$searchObjec = (_parseUrl = parseUrl(video)) === null || _parseUrl === void 0 ? void 0 : (_parseUrl$searchObjec2 = _parseUrl.searchObject) === null || _parseUrl$searchObjec2 === void 0 ? void 0 : _parseUrl$searchObjec2.v) !== null && _parseUrl$searchObjec !== void 0 ? _parseUrl$searchObjec : video;
  var urlStart = enhancedPrivacy ? 'https://www.youtube-nocookie.com' : 'https://www.youtube.com';
  var searchObject = object();

  if (!showControls) {
    searchObject.controls = '0';
  }

  if (startAt) {
    searchObject['amp;start'] = startAt;
  }

  return "".concat(urlStart, "/embed/").concat(id, "?").concat(stringify(searchObject));
}

export { IframeExtension };
